import { useRef, useState, useEffect, useCallback, type FC } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { trackChatOpen } from './helpers/tracking.ts';
import { SESSION_CHAT_STATE_KEY } from './constants.ts';
import { localStorage } from 'dibs-browser-storage';
import { graphql, useFragment } from 'react-relay';
import { updateMakeOfferModalShown } from '../../actions/pdpRespMakeOfferModalActions.js';
import { useIsSellerBrandingTestVariant } from '../helpers/sellerBrandingTestHelpers.ts';
import { useIsTrustMessagingTest2Variant } from '../helpers/trustMessagingTest2Helpers.ts';

// components
import { BuyerChatRespLazy } from 'dibs-buyer-chat/exports/BuyerChatRespLazy.ts';
import { BuyerIncentivesBannerExtendedLazy } from 'dibs-buyer-incentives/exports/BuyerIncentivesBannerExtendedLazy.ts';
import {
    VisibilityTracker,
    type VisibilityChangeCallback,
} from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

// types
import { type PdpRespDealerChatLoader_viewer$key as Viewer } from './__generated__/PdpRespDealerChatLoader_viewer.graphql.ts';

type Props = {
    viewer: Viewer;
    currency: string;
    isCondensedPdp?: boolean;
};

type RelayVariables = {
    isInteractivePdpChat: boolean;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
    makeOfferModal: {
        makeOfferModalShown: boolean;
    };
};

const isMinimizedLocalStorage = (): boolean => {
    const { isMinimized = false } =
        (localStorage.getItem(SESSION_CHAT_STATE_KEY) as { isMinimized?: boolean }) || {};
    return isMinimized;
};

const viewerFragment = graphql`
    fragment PdpRespDealerChatLoader_viewer on Viewer {
        ...BuyerChatRespLazy_viewer @include(if: $isInteractivePdpChat)
    }
`;

export const PdpRespDealerChatLoader: FC<Props> = ({
    viewer: viewerRef,
    currency,
    isCondensedPdp,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const chatVisibilityRef = useRef(null);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isChatVisible, setIsChatVisible] = useState(false);
    const dispatch = useDispatch();

    const { isInteractivePdpChat } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );

    const { makeOfferModalShown } = useSelector((state: ReduxStoreType) => state.makeOfferModal);

    const showSellerBranding = useIsSellerBrandingTestVariant() && !isCondensedPdp;

    const showTrustMessaging = useIsTrustMessagingTest2Variant() && !isCondensedPdp;

    useEffect(() => {
        if (isChatVisible && isInteractivePdpChat) {
            const isInitiallyMinimized = isMinimizedLocalStorage();
            trackChatOpen(isInitiallyMinimized);
        }
    }, [isChatVisible, isInteractivePdpChat]);

    useEffect(() => {
        const isInitiallyMinimized = isMinimizedLocalStorage();
        setIsMinimized(isInitiallyMinimized);
    }, [makeOfferModalShown]);

    const handleVisibilityChange = useCallback(
        ({ isVisible, boundingClientRect, disconnect }: VisibilityChangeCallback) => {
            if (isVisible || boundingClientRect?.top < 0) {
                setIsChatVisible(true);
                disconnect();
            }
        },
        []
    );

    return (
        <>
            <div ref={chatVisibilityRef} />
            <VisibilityTracker
                elementRef={chatVisibilityRef}
                onVisibilityChange={handleVisibilityChange}
            />
            {isChatVisible && isInteractivePdpChat && (
                <div>
                    <BuyerChatRespLazy
                        viewer={viewer}
                        currency={currency}
                        isInitiallyMinimized={isMinimized}
                        onCloseMakeOfferModal={() => dispatch(updateMakeOfferModalShown(false))}
                        onOpenMakeOfferModal={() => dispatch(updateMakeOfferModalShown(true))}
                        showSellerBranding={showSellerBranding}
                        infoBanner={
                            showTrustMessaging ? <BuyerIncentivesBannerExtendedLazy /> : undefined
                        }
                    />
                </div>
            )}
        </>
    );
};
